'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';

// ============================================================================
// WORKFLOW TYPES
// ============================================================================

export interface WorkflowStep {
  id: string;
  title: string;
  description?: string;
  component: React.ReactNode;
  isOptional?: boolean;
  canSkip?: boolean;
}

export interface WorkflowState {
  currentStepIndex: number;
  isLoading: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any -- Wizard step payloads are opaque pass-through; consumers cast to their concrete type at the boundary. Tighten when all workflow consumers are migrated. */
  stepData: Record<string, any>;
  completedSteps: Set<number>;
}

export interface WorkflowContextType extends WorkflowState {
  steps: WorkflowStep[];
  goToStep: (index: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setStepData: (stepId: string, data: any) => void;
  getStepData: (stepId: string) => any;
  setLoading: (loading: boolean) => void;
  markStepComplete: (index: number) => void;
  canProceed: () => boolean;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ============================================================================
// WORKFLOW CONTEXT
// ============================================================================

const WorkflowContext = React.createContext<WorkflowContextType | null>(null);

export function useWorkflow() {
  const context = React.use(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
}

// ============================================================================
// WORKFLOW PROVIDER
// ============================================================================

interface WorkflowProviderProps {
  steps: WorkflowStep[];
  initialStep?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- step payloads are opaque (see WorkflowState above)
  onComplete?: (data: Record<string, any>) => void;
  children: React.ReactNode;
}

export function WorkflowProvider({
  steps,
  initialStep = 0,
  onComplete,
  children
}: WorkflowProviderProps) {
  const [state, setState] = React.useState<WorkflowState>(() => ({
    currentStepIndex: initialStep,
    isLoading: false,
    stepData: {},
    completedSteps: new Set()
  }));

  const goToStep = React.useCallback(
    (index: number) => {
      if (index >= 0 && index < steps.length) {
        setState((prev) => ({ ...prev, currentStepIndex: index }));
      }
    },
    [steps.length]
  );

  const nextStep = React.useCallback(() => {
    setState((prev) => {
      const newIndex = Math.min(prev.currentStepIndex + 1, steps.length - 1);
      if (newIndex === steps.length - 1 && onComplete) {
        // Reached final step, could trigger completion
      }
      return { ...prev, currentStepIndex: newIndex };
    });
  }, [steps.length, onComplete]);

  const prevStep = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStepIndex: Math.max(prev.currentStepIndex - 1, 0)
    }));
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- step payloads are opaque (see WorkflowState above)
  const setStepData = React.useCallback((stepId: string, data: any) => {
    setState((prev) => ({
      ...prev,
      stepData: { ...prev.stepData, [stepId]: data }
    }));
  }, []);

  const getStepData = React.useCallback(
    (stepId: string) => {
      return state.stepData[stepId];
    },
    [state.stepData]
  );

  const setLoading = React.useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, isLoading: loading }));
  }, []);

  const markStepComplete = React.useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      completedSteps: new Set([...Array.from(prev.completedSteps), index])
    }));
  }, []);

  const canProceed = React.useCallback(() => {
    // Add custom validation logic here
    return !state.isLoading;
  }, [state.isLoading]);

  const isFirstStep = React.useCallback(() => {
    return state.currentStepIndex === 0;
  }, [state.currentStepIndex]);

  const isLastStep = React.useCallback(() => {
    return state.currentStepIndex === steps.length - 1;
  }, [state.currentStepIndex, steps.length]);

  const contextValue: WorkflowContextType = {
    ...state,
    steps,
    goToStep,
    nextStep,
    prevStep,
    setStepData,
    getStepData,
    setLoading,
    markStepComplete,
    canProceed,
    isFirstStep,
    isLastStep
  };

  return <WorkflowContext value={contextValue}>{children}</WorkflowContext>;
}

// ============================================================================
// WORKFLOW PROGRESS TRACKER
// ============================================================================

interface WorkflowProgressProps {
  className?: string;
  showLabels?: boolean;
}

export function WorkflowProgress({
  className,
  showLabels = true
}: WorkflowProgressProps) {
  const { steps, currentStepIndex, completedSteps } = useWorkflow();

  return (
    <div
      className={cn('flex items-center justify-center space-x-2', className)}
    >
      {steps.map((step, index) => {
        const isActive = index === currentStepIndex;
        const isCompleted = completedSteps.has(index);
        const isPast = index < currentStepIndex;

        return (
          <div key={step.id} className="flex items-center">
            {/* Step Circle */}
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-all',
                {
                  'bg-primary text-primary-foreground': isActive || isCompleted,
                  'bg-muted text-muted-foreground':
                    !isActive && !isCompleted && !isPast,
                  'bg-primary/20 text-primary': isPast && !isCompleted
                }
              )}
            >
              {isCompleted ? (
                <Check className="h-4 w-4" />
              ) : (
                <span>{index + 1}</span>
              )}
            </div>

            {/* Step Label */}
            {showLabels && (
              <div className="ml-2 hidden sm:block">
                <div
                  className={cn('text-sm font-medium', {
                    'text-foreground': isActive,
                    'text-primary': isCompleted,
                    'text-muted-foreground': !isActive && !isCompleted
                  })}
                >
                  {step.title}
                </div>
              </div>
            )}

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <div
                className={cn('mx-2 h-0.5 w-8 transition-all sm:mx-4 sm:w-12', {
                  'bg-primary': isPast || isCompleted,
                  'bg-muted': !isPast && !isCompleted
                })}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// WORKFLOW NAVIGATION
// ============================================================================

interface WorkflowNavigationProps {
  className?: string;
  onNext?: () => void;
  onPrev?: () => void;
  onSubmit?: () => void;
  nextLabel?: string;
  prevLabel?: string;
  submitLabel?: string;
  showProgress?: boolean;
  disableBack?: boolean;
  hideFooterNav?: boolean;
  hideBack?: boolean;
}

export function WorkflowNavigation({
  className,
  onNext,
  onPrev,
  onSubmit,
  nextLabel = 'Next',
  prevLabel = 'Back',
  submitLabel = 'Submit',
  showProgress = true,
  disableBack = false,
  hideFooterNav = false,
  hideBack = false
}: WorkflowNavigationProps) {
  const { nextStep, prevStep, isFirstStep, isLastStep, canProceed, isLoading } =
    useWorkflow();

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else {
      nextStep();
    }
  };

  const handlePrev = () => {
    if (onPrev) {
      onPrev();
    } else {
      prevStep();
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <div className={cn('mt-auto space-y-4', className)}>
      {/* Progress Tracker */}
      {!hideFooterNav && showProgress && <WorkflowProgress />}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        {!hideFooterNav && !hideBack ? (
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={isFirstStep() || isLoading || disableBack}
            className="min-w-24"
          >
            <ChevronLeft className="h-4 w-4" />
            {prevLabel}
          </Button>
        ) : null}

        <div className="ms-auto flex space-x-2">
          {isLastStep() ? (
            <Button
              size="xs"
              density="compact"
              variant="outline"
              onClick={handleSubmit}
              disabled={!canProceed() || isLoading}
              className="min-w-24"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          ) : (
            <Button
              size="xs"
              density="compact"
              gap="standard"
              onClick={handleNext}
              disabled={!canProceed() || isLoading}
              className="min-w-24"
            >
              {nextLabel}
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// WORKFLOW CONTENT
// ============================================================================

interface WorkflowContentProps {
  className?: string;
  hideStepTitle?: boolean;
}

export function WorkflowContent({
  className,
  hideStepTitle = false
}: WorkflowContentProps) {
  const { steps, currentStepIndex } = useWorkflow();
  const currentStep = steps[currentStepIndex];

  if (!currentStep) {
    return null;
  }

  return (
    <div className={cn('flex grow flex-col space-y-6', className)}>
      {/* Step Header - only show if not hidden (when title is shown elsewhere) */}
      {!hideStepTitle && (
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold tracking-tight">
            {currentStep.title}
          </h2>
          {currentStep.description && (
            <p className="text-muted-foreground">{currentStep.description}</p>
          )}
        </div>
      )}

      {/* Step Content */}
      <div className="flex-1">{currentStep.component}</div>
    </div>
  );
}

// ============================================================================
// BATCH ACTION COMPONENTS
// ============================================================================

export interface BatchActionItem {
  id: string;
  title: string;
  description?: string;
  status: 'pending' | 'loading' | 'success' | 'error';
  data?: unknown;
}

interface BatchActionListProps {
  items: BatchActionItem[];
  onItemAction?: (item: BatchActionItem) => void;
  onBatchAction?: () => void;
  itemActionLabel?: string;
  batchActionLabel?: string;
  className?: string;
}

export function BatchActionList({
  items,
  onItemAction,
  onBatchAction,
  itemActionLabel = 'Send',
  batchActionLabel = 'Send All',
  className
}: BatchActionListProps) {
  const allPending = items.every((item) => item.status === 'pending');
  const anyLoading = items.some((item) => item.status === 'loading');

  return (
    <div className={cn('space-y-4', className)}>
      {/* Batch Action Header */}
      {onBatchAction && (
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <h3 className="text-lg font-medium">
              {items.length} {items.length === 1 ? 'item' : 'items'} ready
            </h3>
            <p className="text-muted-foreground text-sm">
              Process individually or all at once
            </p>
          </div>
          <Button
            size="xs"
            density="compact"
            gap="standard"
            onClick={onBatchAction}
            disabled={!allPending || anyLoading}
          >
            {anyLoading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Processing...
              </>
            ) : (
              batchActionLabel
            )}
          </Button>
        </div>
      )}

      {/* Item List */}
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'flex items-center justify-between rounded-lg border p-4 transition-all',
              {
                'border-primary/20 bg-primary/10': item.status === 'success',
                'border-destructive/20 bg-destructive/10':
                  item.status === 'error',
                'bg-muted/50': item.status === 'loading'
              }
            )}
          >
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h4 className="font-medium">{item.title}</h4>
                {item.status === 'success' && (
                  <Check className="text-primary h-4 w-4" />
                )}
                {item.status === 'loading' && (
                  <Loader2 className="text-primary h-4 w-4 animate-spin" />
                )}
              </div>
              {item.description && (
                <p className="text-muted-foreground text-sm">
                  {item.description}
                </p>
              )}
            </div>

            {onItemAction && (
              <Button
                size="xs"
                density="compact"
                gap="standard"
                variant={item.status === 'success' ? 'ghost' : 'default'}
                onClick={() => onItemAction(item)}
                disabled={
                  item.status === 'loading' || item.status === 'success'
                }
              >
                {item.status === 'loading' ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Sending...
                  </>
                ) : item.status === 'success' ? (
                  'Sent'
                ) : (
                  itemActionLabel
                )}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
